<script>
// Dear developer, before you start to mess around with this file, PLEASE talk to me: shayaulman@gmail.com
import { mapState, mapMutations, mapGetters } from 'vuex';
import StripeService from '@/services/stripe';

import { GATEWAYS, widgetViews } from '@/utils';
import { getUpsellDecision } from '@/services/upsell';
import FormLayout from '@/layouts/FormLayout.vue';
import FormMainDetails from '@/components/FormMainDetails.vue';
import FormDonationItems from '@/components/FormDonationItems.vue';
import FormOften from '@/components/FormOften.vue';
import FormPaymentButtons from '@/components/FormPaymentButtons.vue';
import LocalizationModal from '@/components/LocalizationModal.vue';
import FormEndDate from '@/components/FormEndDate.vue';

// import StandWithIsraelFooter from '@/components/StandWithIsraelFooter.vue';

export default {
  name: 'Form',
  props: {
    firstView: Boolean
  },
  components: {
    FormLayout,
    FormMainDetails,
    FormDonationItems,
    FormOften,
    FormPaymentButtons,
    LocalizationModal,
    FormEndDate
    // StandWithIsraelFooter
  },
  data() {
    return {
      widgetViews,
      stepsThatWereActive: {
        1: true
      },
      showErrors: {},
      areFieldsDirty: false,
      registerOutsideClick: false,
      paddingBottom: 200
    };
  },

  computed: {
    ...mapState(['showPaymentButtons']),
    ...mapState('donation', {
      donation: (state) => state
    }),
    ...mapState('campaign', {
      settings: (campaign) => campaign.settings,
      campaignId: (campaign) => campaign.id,
      campaignCurrency: (campaign) => campaign.currency
    }),
    ...mapState('publicOrgSettings', {
      defaultSettings: (state) => state.settings
    }),
    ...mapGetters('campaign', ['hasActiveGoalMeter', 'shouldSkipAdditionalDetails']),
    ...mapGetters('validation', ['isFirstStepValid']),

    frequency() {
      return this.donation.frequency;
    },
    hideFrequencyToggle() {
      return this.isItemsCheckout || this.settings.frequenciesEnabled?.length < 2;
    },
    checkoutMode() {
      return this.settings.checkoutMode || this.defaultSettings.checkoutMode || 'amounts';
    },
    hasDonationItems() {
      return Boolean(this.settings.donationItems?.length || this.defaultSettings.donationItems?.length);
    },
    isItemsCheckout() {
      return this.checkoutMode === 'items' && this.hasDonationItems;
    },
    hideDurationSettings() {
      return this.isItemsCheckout || this.settings.hideDurationSettings;
    },
    upsellDecision() {
      return getUpsellDecision({
        settings: this.settings,
        orgUpsellSettings: this.defaultSettings.upsell,
        donation: this.donation,
        campaignId: this.campaignId || this.donation.campaignId,
        campaignCurrency: this.campaignCurrency
      });
    },
    hasHandledUpsell() {
      return this.donation.upsell?.originalAmount === this.donation.amount;
    }
  },

  watch: {
    isItemsCheckout: {
      immediate: true,
      handler(isItemsCheckout) {
        if (isItemsCheckout && this.donation.frequency !== 'single') {
          this.$store.dispatch('donation/SET_FREQUENCY_ACTIONS', 'single');
        }
      }
    },
    'settings.frequenciesEnabled': {
      immediate: true,
      handler(frequenciesEnabled) {
        if (this.isItemsCheckout) {
          if (this.donation.frequency !== 'single') {
            this.$store.dispatch('donation/SET_FREQUENCY_ACTIONS', 'single');
          }
          return;
        }

        if (!frequenciesEnabled?.length) return;
        if (!frequenciesEnabled.includes(this.donation.frequency)) {
          this.$store.dispatch('donation/SET_FREQUENCY_ACTIONS', this.settings.frequencyDefaultComputed || frequenciesEnabled[0]);
        }
      }
    },
    frequency: {
      immediate: true,
      async handler(newValue) {
        if (newValue) {
          this.$set(this.showErrors, 1, false);
          this.$set(this.stepsThatWereActive, 2, true);
          await this.$nextTick();
        }
      }
    },
    'donation.amount': {
      immediate: true,
      async handler(newValue) {
        if (newValue) {
          this.$set(this.stepsThatWereActive, 3, true);
          await this.$nextTick();
        }
      }
    }
  },

  methods: {
    ...mapMutations(['SET_WIDGET_VIEW', 'SET_SHOW_PAYMENT_BUTTONS']),
    async donateClicked() {
      this.makeStepsDirty([1, 2, 3]);
      if (!this.isFirstStepValid) {
        return;
      }

      if (this.upsellDecision && !this.hasHandledUpsell) {
        this.SET_WIDGET_VIEW(widgetViews.UPSELL);
        return;
      }

      // Skip additional details if all options are hidden
      if (this.shouldSkipAdditionalDetails) {
        this.SET_SHOW_PAYMENT_BUTTONS(true);
        return;
      }

      // Navigate to additional details view instead of showing payment buttons
      this.SET_WIDGET_VIEW(widgetViews.ADDITIONAL_DETAILS);
    },
    handleAmountClick() {
      this.$set(this.stepsThatWereActive, 1, true);
      if (!this.stepsThatWereActive[2] && !this.showErrors[2]) {
        return;
      }
    },
    handleOftenClick() {
      this.$set(this.stepsThatWereActive, 2, true);
    },
    handlePaymentSelectionClick() {
      this.$set(this.stepsThatWereActive, 3, true);
    },
    handleMainDetailsOutsideClick(event) {
      if (!this.registerOutsideClick) {
        return;
      }
      // Yes, I know, this is very fragile, do you have a better (but a complete one) solution?
      let afterSectionLayer = 420;
      if (this.hasActiveGoalMeter) {
        afterSectionLayer = 530;
      }
      const shouldDisplayError = !this.donation.amount && event.screenY > afterSectionLayer;
      if (shouldDisplayError) {
        this.displayError(1, 'formAmount');
      }
    },
    handleOftenOutsideClick(event) {
      if (!this.registerOutsideClick) {
        return;
      }
      const shouldDisplayError = !this.oftenSelected && !this.showErrors[1] && event.layerY > 430;
      if (shouldDisplayError) {
        this.displayError(2, 'formOften');
      }
    },

    displayError(stepNumber) {
      this.$set(this.showErrors, stepNumber, true);
    },
    makeFieldsDirty() {
      this.areFieldsDirty = true;
      setTimeout(() => {
        this.areFieldsDirty = false; // kinda hacky, but... (thanks tabNine for the comment)
      });
    },
    makeStepsDirty(stepsArray) {
      stepsArray.forEach((step) => {
        this.$set(this.showErrors, step, true);
      });
      this.makeFieldsDirty();
    }
  },
  created() {
    // Load Stripe in advance to avoid delay on Credit Card screen in case the user selects it
    if (this.donation.CCGateway === GATEWAYS.STRIPE.provider) {
      StripeService.getStripeObject();
    }
  },
  mounted() {
    this.paddingBottom = this.$refs.footerSticky.$el.offsetHeight;
  }
};
</script>

<template>
  <form-layout show-header :show-logo="false" :style="`padding-bottom: ${paddingBottom}px`">
    <section ref="form" class="app-form">
      <div class="form-container">
        <div class="form-content">
          <form-often
            v-if="!hideFrequencyToggle"
            v-click-outside="handleOftenOutsideClick"
            @click.native="handleOftenClick"
            :class="[{ active: stepsThatWereActive[1] }]"
            :active="stepsThatWereActive[1]"
            :show-error="showErrors[1]"
            ref="formOften"
          />

          <transition name="expandx2" v-if="!hideDurationSettings">
            <form-end-date />
          </transition>

          <form-main-details
            v-if="!isItemsCheckout"
            v-click-outside="handleMainDetailsOutsideClick"
            @click.native="handleAmountClick"
            :class="[{ active: stepsThatWereActive[2] }]"
            :active="stepsThatWereActive[2]"
            :show-error="showErrors[2]"
            :fields-dirty="areFieldsDirty"
            ref="formAmount"
          />

          <form-donation-items
            v-else
            v-click-outside="handleMainDetailsOutsideClick"
            @click.native="handleAmountClick"
            :class="[{ active: stepsThatWereActive[2] }]"
            :active="stepsThatWereActive[2]"
            :show-error="showErrors[2]"
            ref="formAmount"
          />
        </div>
      </div>
    </section>

    <template #footer>
      <footer-sticky @donate-clicked="donateClicked" ref="footerSticky">
        <template #content> </template>

        <!-- <template #fundraiser-cta>
          <button
            v-if="!settings.p2pDisabled"
            @click="SET_WIDGET_VIEW(widgetViews.FUNDRAISER_SIGNUP)"
            class="mx-auto flex mt-5 cursor-pointer underline text-black-01"
            data-cy="fundraiser-cta"
          >
            {{ $t('fundraiser.cta') }}
          </button>
        </template> -->
      </footer-sticky>
      <form-payment-buttons @donation-attempt="makeFieldsDirty" @payment-method-selected="makeStepsDirty([1, 2, 3])" ref="paymentSection" />
      <localization-modal />
    </template>
  </form-layout>
</template>
